// src/store/meetings/meetings.thunks.ts

import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import meetingsService from "@/services/meetings.service";
import {
  fetchMeetingsStart,
  fetchMeetingsSuccess,
  fetchMeetingsError,
  setPage,
} from "./meetings.slice";
import type { AppDispatch, RootState } from "@/store";
import { TMeeting, TCreateMeetingPayload } from "@/models/meetings.model";
import { getErrorFromBackend } from "@/utils/errors";

/**
 * Fetches meetings from the API with pagination and filters.
 *
 * - If `force` is false (default) and the store is already initialized,
 *   it skips the request and reuses cached data.
 * - If `force` is true, it always fetches fresh data from the API.
 * - Accepts an optional `requestedPage` to fetch a specific page.
 */
export const fetchMeetings =
  (requestedPage?: number, force: boolean = false) =>
    async (dispatch: Dispatch, getState: () => RootState) => {
      const state = getState();
      const { filters, page: currentPage, limit, initialized, loading } = state.meetings;

      // Skip if already loading
      if (loading) return;

      // Skip if already initialized and not forced
      if (initialized && !force && !requestedPage) return;

      const page = requestedPage || currentPage;

      try {
        dispatch(fetchMeetingsStart());

        const response = await meetingsService.getAll({
          ...filters,
          page,
          limit,
        });

        dispatch(fetchMeetingsSuccess(response));
      } catch (error: unknown) {
        dispatch(fetchMeetingsError(getErrorFromBackend(error)));
        throw error;
      }
    };

export const createMeeting = createAsyncThunk<
  TMeeting,
  TCreateMeetingPayload,
  { dispatch: AppDispatch; state: RootState }
>("meetings/createMeeting", async (payload, { dispatch }) => {
  const meeting = await meetingsService.create(payload);

  // After successful creation, refetch page 1 to ensure correct ordering
  // and pagination consistency from the backend
  dispatch(setPage(1));
  dispatch(fetchMeetings(1, true));

  return meeting;
});

export const updateMeeting = createAsyncThunk<
  TMeeting,
  {
    id: number;
    updates: Partial<
      Omit<TMeeting, "id" | "uuid" | "companyId" | "created" | "updated">
    >;
  }
>("meetings/updateMeeting", async ({ id, updates }) => {
  const updatedMeeting = await meetingsService.updateById(id, updates);
  return updatedMeeting;
});

export const deleteMeeting = createAsyncThunk<
  number,
  number,
  { dispatch: AppDispatch; state: RootState }
>("meetings/deleteMeeting", async (id, { dispatch, getState }) => {
  await meetingsService.deleteById(id);

  // Refetch current page to fill the gap left by the deleted meeting
  const state = getState();
  const { page } = state.meetings;
  dispatch(fetchMeetings(page, true));

  return id;
});

export const setMeetingStatus = createAsyncThunk<
  TMeeting,
  { id: number; status: string; reason?: string }
>("meetings/setMeetingStatus", async ({ id, status, reason }) => {
  const updatedMeeting = await meetingsService.setStatusById(id, status, reason);
  return updatedMeeting;
});